<template>
  <div class="flex h-full flex-col justify-between">
    <div class="flex flex-col">
      <h3 class="body-1-bold mb-16">
        {{ i18n(translations.title) }}
      </h3>
      <p class="body-2 mb-24">
        {{ i18n(translations.subtitle) }}
      </p>

      <div class="mb-16">
        <h4 class="body-1-bold mb-8">
          {{ i18n(translations.orderInfo) }} {{ order.id }}
        </h4>

        <div class="mb-4 flex justify-between">
          <span class="text-static-default-mid">
            {{ i18n(translations.customer) }}:
          </span>

          <span class="body-2 flex items-center gap-8">
            <Flag :country-code="order.market" />
            <span>
              {{ order.client.firstName }}
              {{ order.client.lastName }}
            </span>
          </span>
        </div>

        <div class="flex justify-between">
          <span class="text-static-default-mid"
            >{{ i18n(translations.orderDate) }}:</span
          >
          <span class="body-2">{{ formatDate(order.creationDate) }}</span>
        </div>
      </div>

      <div class="mb-24 flex flex-col gap-16">
        <div
          v-for="orderLine in selectedOrderLines"
          :key="orderLine.id"
          class="bg-static-default-low rounded-lg flex flex-col gap-12 p-16"
        >
          <h5 class="body-1-bold">{{ orderLine.productName }}</h5>

          <div class="flex items-center justify-between">
            <span class="body-2"> {{ i18n(translations.sku) }}: </span>
            <span class="break-all pl-12 text-right">{{ orderLine.sku }}</span>
          </div>

          <div class="flex items-center justify-between">
            <span class="body-2"> {{ i18n(translations.quantity) }}: </span>
            <span>{{ orderLine.quantity }}</span>
          </div>

          <div class="flex items-center justify-between">
            <span class="body-2"> {{ i18n(translations.appearance) }}: </span>
            <RevTag :label="orderLine.appearance.label" variant="secondary" />
          </div>

          <div class="flex items-center justify-between">
            <span class="body-2"> {{ i18n(translations.appearance) }}: </span>
            <RevTag :label="orderLine.grade.label" variant="secondary" />
          </div>
        </div>
      </div>

      <p class="body-2 mb-16">
        {{ i18n(translations.stockWarning) }}
      </p>
    </div>

    <RevButton
      data-qa="cancel-order-button"
      full-width="always"
      :is-loading
      variant="primaryDestructive"
      @click="handleCancelOrder"
    >
      {{ i18n(translations.cancelOrder) }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { cancelOrderLine } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import type { GetSellerXpOrderDetails } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-pages.types'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { closeModal } from '@ds/components/ModalBase'
import { RevTag } from '@ds/components/Tag'

import type { Order } from '~/scopes/orders/pages/components/Tab/Tab.types'

import { MODAL_NAME } from '../../../FixOrderIssueDrawer.constants'

import translations from './CancelOrderLines.translations'

const { openErrorToast, openSuccessToast } = useTheToast()
const props = defineProps<{
  order: Order
  orderLines: GetSellerXpOrderDetails['orderLines']
  selectedOrderLinesIds: string[]
}>()

const emit = defineEmits<{
  (e: 'order-cancelled'): void
}>()

const i18n = useI18n()

const isLoading = ref(false)

const selectedOrderLines = computed(() => {
  return props.orderLines.filter((orderLine) =>
    props.selectedOrderLinesIds.includes(orderLine.id.toString()),
  )
})

function formatDate(dateString: string) {
  const date = new Date(dateString)

  return new Intl.DateTimeFormat('default', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

async function handleCancelOrder() {
  try {
    isLoading.value = true

    await $httpFetch(cancelOrderLine, {
      pathParams: {
        orderId: props.order.id,
      },
      body: {
        items: props.selectedOrderLinesIds.map((id) => ({
          id,
        })),
      },
    })

    openSuccessToast({
      title: i18n(translations.successMessageTitle),
      content: i18n(translations.successMessageContent),
    })

    emit('order-cancelled')

    closeModal(MODAL_NAME + props.order.id)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isLoading.value = false
  }
}
</script>
